import { useMemo, useEffect, useRef, useState } from 'react';
import {
  forceSimulation,
  forceCollide,
  forceX,
  forceY,
  SimulationNodeDatum,
} from 'd3-force';
import { WorkingState, CapabilityEntry } from '../../../types/persona_engine';
import { migrateCapabilities } from '../../../utils/persona_engine_utils';
import {
  NodeType,
  CLUSTER_POSITIONS,
  NODE_CLUSTER,
  NODE_COLORS,
  NODE_RADIUS,
  computeClusterPositions,
  getTagColor,
} from './constants';

export interface InnerLineageItem {
  id: string;
  label: string;
  data: any;
  section: 'origin' | 'tag' | 'suggested_name';
  dx: number;
  dy: number;
  tagColor?: string;
}

export interface InnerIdentityItem {
  id: string;
  label: string;
  data: any;
  pillar: 'character_bible' | 'tethic' | 'pattern' | 'antipattern';
  dx: number;
  dy: number;
  // Cross-link targets (ids of items this one reinforces/conflicts with)
  reinforces?: string[];
  conflicts?: string[];
}

export interface InnerProbe {
  id: string;
  label: string;
  data: any;
  dx: number;
  dy: number;
}

export interface InnerCapability {
  id: string;
  label: string;
  data: any;
  dx: number;
  dy: number;
}

export interface InnerControl {
  id: string;
  label: string;
  data: any;
  dx: number;
  dy: number;
  colType: string;   // 'slider' | 'toggle' | 'select' | 'text'
}

export interface MapNode extends SimulationNodeDatum {
  id: string;
  type: NodeType;
  clusterId: string;
  label: string;
  radius: number;
  color: string;
  data: any;
  // settled position (written by simulation)
  x: number;
  y: number;
  // For cluster nodes: settled positions of inner children
  innerProbes?: InnerProbe[];
  innerCapabilities?: InnerCapability[];
  innerControls?: InnerControl[];
  innerIdentity?: InnerIdentityItem[];
  innerLineage?: InnerLineageItem[];
  // Dynamic parallelogram sizing (identity_cluster only)
  paraW?: number;
  paraH?: number;
  paraSkew?: number;
  // remix depth (lineage_cluster only)
  remixDepth?: number;
}

function buildNodes(
  state: WorkingState,
  clusterPositions: Record<string, { x: number; y: number; color: string; label: string }>,
): MapNode[] {
  const nodes: MapNode[] = [];

  const add = (
    id: string,
    type: NodeType,
    label: string,
    data: any,
  ) => {
    const clusterId = NODE_CLUSTER[type];
    const center = clusterPositions[clusterId];
    const radius = NODE_RADIUS[type] ?? 12;
    nodes.push({
      id,
      type,
      clusterId,
      label,
      radius,
      color: NODE_COLORS[type],
      data,
      // start near cluster center with small jitter
      x: center.x + (Math.random() - 0.5) * 40,
      y: center.y + (Math.random() - 0.5) * 40,
    });
  };

  // Identity cluster — parallelogram containing character bible + 3 pillars
  {
    const tethics     = state.tethics     || [];
    const patterns    = state.patterns    || [];
    const antipatterns = state.antipatterns || [];
    const bible       = state.character_bible || '';

    const hasIdentity = bible || tethics.length || patterns.length || antipatterns.length;
    if (hasIdentity) {
      const clusterId = NODE_CLUSTER['identity_cluster'];
      const center    = clusterPositions[clusterId];

      // ── Dynamic parallelogram sizing ──────────────────────────────────────
      // Width scales with bible word count (clamped 140–260px)
      const wordCount = bible.trim().split(/\s+/).filter(Boolean).length;
      const paraW = Math.max(140, Math.min(260, 140 + wordCount * 0.18));

      // Height scales with max pillar length (clamped 100–200px)
      const maxPillar = Math.max(tethics.length, patterns.length, antipatterns.length, 1);
      const paraH = Math.max(100, Math.min(200, 80 + maxPillar * 16));

      // Skew encodes tethics/patterns tension ratio
      // More tethics than patterns → more skewed (rigid persona)
      const tensionRatio = tethics.length / Math.max(patterns.length, 1);
      const paraSkew = Math.max(8, Math.min(32, tensionRatio * 16));

      // Collision radius = half-diagonal of parallelogram
      const CLUSTER_R = Math.sqrt((paraW / 2) ** 2 + (paraH / 2) ** 2);

      // ── Per-pillar mini force simulations ────────────────────────────────
      const ROW_SPACING = 18;
      const COL_SPACING = paraW / 4;  // 3 cols centered in paraW

      const buildPillarNodes = (
        items: string[],
        pillar: InnerIdentityItem['pillar'],
        colX: number,
      ): InnerIdentityItem[] => {
        if (!items.length) return [];
        const rawNodes = items.map((item, i) => ({
          id: `${pillar}_${i}`,
          label: item.length > 28 ? item.slice(0, 28) + '…' : item,
          data: item,
          pillar,
          x: colX + (Math.random() - 0.5) * 8,
          y: (i - (items.length - 1) / 2) * ROW_SPACING,
          dx: 0,
          dy: 0,
        }));

        // Mini force sim — keeps items in column, separates vertically
        const sim = forceSimulation(rawNodes as any)
          .force('x', forceX(colX).strength(0.9))
          .force('y', forceY((n: any) => (rawNodes.indexOf(n) - (rawNodes.length - 1) / 2) * ROW_SPACING).strength(0.6))
          .force('collide', forceCollide(7).strength(0.8))
          .stop();
        for (let i = 0; i < 50; i++) sim.tick();

        // Clamp to pillar column bounds
        rawNodes.forEach((n: any) => {
          n.dx = Math.max(colX - 18, Math.min(colX + 18, n.x));
          n.dy = Math.max(-paraH / 2 + 24, Math.min(paraH / 2 - 8, n.y));
        });

        return rawNodes.map((n: any) => ({
          id: n.id,
          label: n.label,
          data: n.data,
          pillar,
          dx: n.dx,
          dy: n.dy,
        }));
      };

      const tethicNodes    = buildPillarNodes(tethics,      'tethic',      -COL_SPACING);
      const patternNodes   = buildPillarNodes(patterns,     'pattern',     0);
      const antiNodes      = buildPillarNodes(antipatterns, 'antipattern', COL_SPACING);

      // Character bible header item
      const bibleItem: InnerIdentityItem | null = bible ? {
        id: 'character_bible',
        label: bible.length > 40 ? bible.slice(0, 40) + '…' : bible,
        data: bible,
        pillar: 'character_bible',
        dx: 0,
        dy: -(paraH / 2) + 10,
      } : null;

      // ── Cross-link computation ────────────────────────────────────────────
      // Tethics reinforce antipatterns that share keywords
      // Patterns conflict with antipatterns that share keywords
      const extractKeywords = (s: string) =>
        s.toLowerCase().split(/\W+/).filter(w => w.length > 4);

      tethicNodes.forEach(t => {
        const tKw = extractKeywords(t.data);
        t.reinforces = antiNodes
          .filter(a => extractKeywords(a.data).some(k => tKw.includes(k)))
          .map(a => a.id);
      });

      patternNodes.forEach(p => {
        const pKw = extractKeywords(p.data);
        p.conflicts = antiNodes
          .filter(a => extractKeywords(a.data).some(k => pKw.includes(k)))
          .map(a => a.id);
      });

      const innerIdentity: InnerIdentityItem[] = [
        ...(bibleItem ? [bibleItem] : []),
        ...tethicNodes,
        ...patternNodes,
        ...antiNodes,
      ];

      const totalItems = tethics.length + patterns.length + antipatterns.length + (bible ? 1 : 0);

      nodes.push({
        id: 'identity_cluster',
        type: 'identity_cluster',
        clusterId,
        label: `Identity Core (${totalItems})`,
        radius: CLUSTER_R,
        color: NODE_COLORS['identity_cluster'],
        data: { bible, tethics, patterns, antipatterns },
        x: center.x + (Math.random() - 0.5) * 20,
        y: center.y + (Math.random() - 0.5) * 20,
        innerIdentity,
        paraW,
        paraH,
        paraSkew,
      });
    }
  }

  // Tuning — flat ui_control nodes replaced by controls_cluster below

  // Artifact
  if (state.system_prompt) {
    add('system_prompt', 'system_prompt', 'System Prompt', state.system_prompt);
  }
  if (state.controller_prompt) {
    add('controller_prompt', 'controller_prompt', 'Controller Prompt', state.controller_prompt);
  }

  // Probes — one outer container node that carries inner probe positions
  if ((state.probes || []).length > 0) {
    const clusterId = NODE_CLUSTER['probe_cluster'];
    const center = clusterPositions[clusterId];
    const probeList = state.probes!;

    const CLUSTER_R = NODE_RADIUS['probe_cluster'];

    // Grid layout — dx/dy used only for tree-browser fly-to, not for rendering
    // (ProbeClusterOverlay uses a CSS grid anchored to cluster center instead)
    const COLS = Math.max(1, Math.min(3, Math.ceil(Math.sqrt(probeList.length))));
    const CARD_W = 160;
    const CARD_H = 72;
    const GAP = 8;

    const innerProbes = probeList.map((p, i) => {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const totalCols = Math.min(COLS, probeList.length);
      const totalRows = Math.ceil(probeList.length / COLS);
      const dx = (col - (totalCols - 1) / 2) * (CARD_W + GAP);
      const dy = (row - (totalRows - 1) / 2) * (CARD_H + GAP);
      return { id: p.id, label: p.label, data: p, dx, dy };
    });

    nodes.push({
      id: 'probe_cluster',
      type: 'probe_cluster',
      clusterId,
      label: `Probes (${probeList.length})`,
      radius: CLUSTER_R,
      color: NODE_COLORS['probe_cluster'],
      data: probeList,
      x: center.x + (Math.random() - 0.5) * 20,
      y: center.y + (Math.random() - 0.5) * 20,
      innerProbes,
    });
  }

  // Capabilities cluster — mini d3-force scatter
  const caps = migrateCapabilities(state.capabilities);
  const capEntries = Object.values(caps);
  if (capEntries.length > 0) {
    const clusterId = NODE_CLUSTER['capability_cluster'];
    const center = clusterPositions[clusterId];
    const CLUSTER_R = NODE_RADIUS['capability_cluster'];
    const DOT_R = 5;

    const innerNodes = capEntries.map((entry) => ({
      id: `cap_${entry.key}`,
      label: entry.label,
      data: entry,
      x: (Math.random() - 0.5) * CLUSTER_R * 0.5,
      y: (Math.random() - 0.5) * CLUSTER_R * 0.5,
    }));

    const innerSim = forceSimulation(innerNodes as any)
      .force('x', forceX(0).strength(0.3))
      .force('y', forceY(0).strength(0.3))
      .force('collide', forceCollide(DOT_R + 4).strength(0.9))
      .stop();

    for (let i = 0; i < 200; i++) innerSim.tick();

    innerNodes.forEach((n: any) => {
      const dist = Math.sqrt(n.x * n.x + n.y * n.y);
      const maxDist = CLUSTER_R - DOT_R - 4;
      if (dist > maxDist) {
        const scale = maxDist / dist;
        n.x *= scale;
        n.y *= scale;
      }
    });

    nodes.push({
      id: 'capability_cluster',
      type: 'capability_cluster',
      clusterId,
      label: `Capabilities (${capEntries.length})`,
      radius: CLUSTER_R,
      color: NODE_COLORS['capability_cluster'],
      data: capEntries,
      x: center.x + (Math.random() - 0.5) * 20,
      y: center.y + (Math.random() - 0.5) * 20,
      innerCapabilities: innerNodes.map((n: any) => ({
        id: n.id,
        label: n.label,
        data: n.data,
        dx: n.x,
        dy: n.y,
      })),
    });
  }

  // Controls cluster — type-grouped columns with per-pillar mini force sims
  const controls = state.ui_schema || [];
  if (controls.length > 0) {
    const clusterId = NODE_CLUSTER['controls_cluster'];
    const center    = clusterPositions[clusterId];

    // Dynamic radius: scales with control count, clamped 72–110px
    const CLUSTER_R = Math.max(72, Math.min(110, 60 + controls.length * 6));

    const TYPE_ORDER = ['slider', 'toggle', 'select', 'text'];
    const ROW_SPACING = 20;

    // Bucket by type
    const buckets: Record<string, typeof controls> = {};
    TYPE_ORDER.forEach((t) => { buckets[t] = []; });
    controls.forEach((c) => {
      const t = TYPE_ORDER.includes(c.type) ? c.type : 'text';
      buckets[t].push(c);
    });

    const activeCols = TYPE_ORDER.filter((t) => buckets[t].length > 0);
    const COL_SPACING = Math.min(48, (CLUSTER_R * 1.6) / Math.max(activeCols.length, 1));
    const totalW = (activeCols.length - 1) * COL_SPACING;

    const buildControlPillar = (
      items: typeof controls,
      colType: string,
      colX: number,
    ): InnerControl[] => {
      if (!items.length) return [];

      const rawNodes = items.map((c, i) => ({
        id: c.id,
        label: c.label,
        data: c,
        colType,
        x: colX + (Math.random() - 0.5) * 6,
        y: (i - (items.length - 1) / 2) * ROW_SPACING,
        dx: 0,
        dy: 0,
      }));

      // Mini force sim — keeps items in column, separates vertically
      const sim = forceSimulation(rawNodes as any)
        .force('x', forceX(colX).strength(0.95))
        .force('y', forceY((n: any) =>
          (rawNodes.indexOf(n) - (rawNodes.length - 1) / 2) * ROW_SPACING
        ).strength(0.7))
        .force('collide', forceCollide(8).strength(0.8))
        .stop();
      for (let i = 0; i < 50; i++) sim.tick();

      // Clamp to cluster bounds
      rawNodes.forEach((n: any) => {
        n.dx = Math.max(colX - 20, Math.min(colX + 20, n.x));
        n.dy = Math.max(-CLUSTER_R + 20, Math.min(CLUSTER_R - 8, n.y));
      });

      return rawNodes.map((n: any) => ({
        id: n.id,
        label: n.label,
        data: n.data,
        colType: n.colType,
        dx: n.dx,
        dy: n.dy,
      }));
    };

    const innerControls: InnerControl[] = [];
    activeCols.forEach((colType, colIdx) => {
      const colX = -totalW / 2 + colIdx * COL_SPACING;
      const pillar = buildControlPillar(buckets[colType], colType, colX);
      innerControls.push(...pillar);
    });

    nodes.push({
      id: 'controls_cluster',
      type: 'controls_cluster',
      clusterId,
      label: `Controls (${controls.length})`,
      radius: CLUSTER_R,
      color: NODE_COLORS['controls_cluster'],
      data: { controls, activeCols, colSpacing: COL_SPACING, totalW },
      x: center.x + (Math.random() - 0.5) * 20,
      y: center.y + (Math.random() - 0.5) * 20,
      innerControls,
    });
  }

  // Lineage cluster — hexagon containing origin + tags + suggested names
  {
    const tags           = state.tags           || [];
    const suggestedNames = state.suggested_names || [];
    const originId       = state.origin_id       || null;
    const remixChain     = (state as any).remix_chain || [];

    const hasLineage = tags.length || suggestedNames.length || originId;
    if (hasLineage) {
      const clusterId = NODE_CLUSTER['lineage_cluster'];
      const center    = clusterPositions[clusterId];

      const totalItems = tags.length + suggestedNames.length + (originId ? 1 : 0);
      const CLUSTER_R  = Math.max(60, Math.min(100, 50 + totalItems * 5));
      const remixDepth = remixChain.length;

      const innerLineage: InnerLineageItem[] = [];

      // Origin bar (top of hexagon)
      if (originId) {
        innerLineage.push({
          id: 'origin_id',
          label: `🔁 ${originId.slice(0, 8)}`,
          data: originId,
          section: 'origin',
          dx: 0,
          dy: -(CLUSTER_R * 0.6),
        });
      }

      // Tags — mini force scatter in center zone
      if (tags.length > 0) {
        const TAG_ZONE_R = CLUSTER_R * 0.55;
        const rawTagNodes = tags.map((tag, i) => ({
          id: `tag_${i}`,
          label: tag,
          data: tag,
          section: 'tag' as const,
          tagColor: getTagColor(tag),
          x: (Math.random() - 0.5) * TAG_ZONE_R,
          y: (Math.random() - 0.5) * TAG_ZONE_R * 0.6,
          dx: 0,
          dy: 0,
        }));

        const tagSim = forceSimulation(rawTagNodes as any)
          .force('x', forceX(0).strength(0.4))
          .force('y', forceY(0).strength(0.4))
          .force('collide', forceCollide(10).strength(0.9))
          .stop();
        for (let i = 0; i < 80; i++) tagSim.tick();

        rawTagNodes.forEach((n: any) => {
          const dist = Math.sqrt(n.x * n.x + n.y * n.y);
          const maxDist = TAG_ZONE_R - 8;
          if (dist > maxDist) {
            const scale = maxDist / dist;
            n.x *= scale;
            n.y *= scale;
          }
          n.dx = n.x;
          n.dy = n.y + (originId ? 8 : 0);
        });

        rawTagNodes.forEach(n => {
          innerLineage.push({
            id: n.id,
            label: n.label,
            data: n.data,
            section: 'tag',
            tagColor: n.tagColor,
            dx: (n as any).dx,
            dy: (n as any).dy,
          });
        });
      }

      // Suggested names — vertical list on bottom arc
      if (suggestedNames.length > 0) {
        const NAME_SPACING = 16;
        const startY = CLUSTER_R * 0.3;
        suggestedNames.forEach((name, i) => {
          innerLineage.push({
            id: `sname_${i}`,
            label: name.length > 18 ? name.slice(0, 18) + '…' : name,
            data: name,
            section: 'suggested_name',
            dx: 0,
            dy: startY + i * NAME_SPACING,
          });
        });
      }

      nodes.push({
        id: 'lineage_cluster',
        type: 'lineage_cluster',
        clusterId,
        label: `Lineage (${totalItems})`,
        radius: CLUSTER_R,
        color: NODE_COLORS['lineage_cluster'],
        data: { tags, suggestedNames, originId, remixChain },
        x: center.x + (Math.random() - 0.5) * 20,
        y: center.y + (Math.random() - 0.5) * 20,
        innerLineage,
        remixDepth,
      });
    }
  }

  return nodes;
}

/**
 * Runs a per-cluster d3-force simulation and returns settled node positions.
 * Re-runs only when node count changes (not on value changes).
 * Pass canvas dimensions so cluster positions are viewport-relative.
 */
export function useMapLayout(
  state: WorkingState | null,
  canvasWidth: number = 1200,
  canvasHeight: number = 700,
): MapNode[] {
  const [settled, setSettled] = useState<MapNode[]>([]);
  const prevCountRef = useRef<number>(-1);
  const prevDimsRef = useRef<string>('');

  const nodeCount = useMemo(() => {
    if (!state) return 0;
    const hasIdentity = !!(
      state.character_bible ||
      (state.tethics?.length ?? 0) > 0 ||
      (state.patterns?.length ?? 0) > 0 ||
      (state.antipatterns?.length ?? 0) > 0
    );
    const hasLineage = !!(
      (state.tags?.length ?? 0) > 0 ||
      (state.suggested_names?.length ?? 0) > 0 ||
      state.origin_id
    );
    return (
      (hasIdentity ? 1 : 0) +
      ((state.ui_schema?.length ?? 0) > 0 ? 1 : 0) +
      (state.system_prompt ? 1 : 0) +
      (state.controller_prompt ? 1 : 0) +
      ((state.probes?.length ?? 0) > 0 ? 1 : 0) +
      (Object.keys(state.capabilities || {}).length > 0 ? 1 : 0) +
      // lineage_cluster counts as 1 (not N items)
      (hasLineage ? 1 : 0)
    );
  }, [state]);

  useEffect(() => {
    const dimsKey = `${canvasWidth}x${canvasHeight}`;
    const countChanged = nodeCount !== prevCountRef.current;
    const dimsChanged = dimsKey !== prevDimsRef.current;
    if (!state || (!countChanged && !dimsChanged)) return;
    prevCountRef.current = nodeCount;
    prevDimsRef.current = dimsKey;

    const clusterPositions = computeClusterPositions(canvasWidth, canvasHeight);
    const nodes = buildNodes(state, clusterPositions);
    if (nodes.length === 0) {
      setSettled([]);
      return;
    }

    // Run one simulation per cluster
    const clusterIds = [...new Set(nodes.map((n) => n.clusterId))];

    clusterIds.forEach((clusterId) => {
      const clusterNodes = nodes.filter((n) => n.clusterId === clusterId);
      const center = clusterPositions[clusterId];

      const sim = forceSimulation(clusterNodes)
        .force('x', forceX(center.x).strength(0.3))
        .force('y', forceY(center.y).strength(0.3))
        .force('collide', forceCollide<MapNode>((n) => n.radius + 4).strength(0.8))
        .stop();

      // Run synchronously — 120 ticks is enough for small clusters
      for (let i = 0; i < 120; i++) sim.tick();
    });

    setSettled([...nodes]);
  }, [nodeCount, state]);

  return settled;
}
